/* ============================================================================
   PHSearch — motor de búsqueda cliente de PadelHouse (e-Growth, 2026-07-23)

   Extraído de search-modal.liquid para poder compartirlo entre el buscador a
   pantalla completa y el quiz "Encuentra tu pala". Busca en local sobre el
   catálogo completo (products.json paginado, cacheado en sessionStorage),
   con corrección de erratas, intención→tags y scoring por campo.

   API pública (window.PHSearch):
     setRoot(root)        raíz de rutas ('' en tienda sin markets)
     carga()              Promise<catálogo indexado> (cache 30 min)
     busca(texto)         {items, etiqueta, parcial, correccion}
     sugiere(texto, n)    [{texto, tipo}] para el autocompletado
     filtra(items, sel)   aplica facetas {marca, nivel, juego, forma}
     FACETAS              definición de facetas para pintar chips
     vendors()            marcas únicas del catálogo (ordenadas)
     recomendados()       Promise<items> de la colección destacada
     formatEuro(v)        "€339,99" (formato de la home)
     imgUrl(src, w)       URL CDN con parámetro de ancho
   ========================================================================== */
(() => {
  'use strict';
  if (window.PHSearch) return;

  let ROOT = '';
  const CACHE_MS = 30 * 60 * 1000;
  const COLECCION_RECO = 'palas-premium-300';

  /* ---- Carrusel de recomendados del estado vacío ----
     Antes eran 10 palas premium seguidas: todas de 300-450 €, medio catálogo de
     Bullpadel y Nox repetido, y cero ticket bajo. Ahora se mezcla el catálogo por
     tipo de producto para que el que abre el buscador sin escribir nada vea
     también con qué acompañar la pala.

     Las cuotas se cumplen EN ORDEN hasta llenar RECO_TOTAL. Si un grupo no tiene
     stock suficiente, lo que sobra se rellena con palas al final, así que el
     carrusel nunca sale corto. Para cambiar la mezcla, toca solo esta tabla.

     `tipos` son product_type tal cual están en Shopify — ojo que conviven
     'Paleteros' y 'Paletero' (un producto suelto mal tipado). */
  const RECO_TOTAL = 16;
  const RECO_GRUPOS = [
    { n: 5, tipos: ['Palas de pádel'] },
    { n: 2, tipos: ['Packs'] },
    { n: 2, tipos: ['Paleteros', 'Paletero', 'Mochilas', 'Neceseres'] },
    { n: 2, tipos: ['Bolas'] },
    { n: 3, tipos: ['Grips', 'Muñequeras', 'Accesorios', 'Presurizadores'] },
    { n: 2, tipos: ['Balones', 'Rodilleras Voley'] }
  ];

  const sinTildes = (s) => String(s).toLowerCase().normalize('NFD')
    .replace(/[̀-ͯ]/g, '').trim();

  /* Erratas y sinónimos. Esta tabla es solo el atajo para lo que se repite; lo
     que no esté aquí lo corrige igualmente `corrigePalabra` contra el
     vocabulario real del catálogo, así que no hay que mantenerla exhaustiva.

     El destino de cada entrada TIENE que ser una palabra que exista de verdad en
     algún título, marca, tipo o tag, o la corrección deja la búsqueda peor que
     como estaba. */
  const ERRATAS = {
    /* Sinónimos: cómo lo llama el cliente -> cómo está en el catálogo */
    raqueta: 'pala', raquetas: 'pala', raquetero: 'paletero',
    bolsa: 'paletero', bolsas: 'paletero', macuto: 'paletero', racketbag: 'paletero',
    funda: 'paletero', neceser: 'neceser', mochilas: 'mochila',
    agarre: 'grip', cinta: 'overgrip', cintas: 'overgrip', empunadura: 'grip',
    antivibracion: 'antivibrador', vibrador: 'antivibrador', vibraciones: 'antivibrador',
    proteccion: 'protector', protectores: 'protector',
    volley: 'voley', voleyball: 'voley', voleybol: 'voley', balon: 'balones',
    rodillera: 'rodilleras', calcetin: 'calcetines', plantilla: 'plantillas',
    presurizador: 'presurizadores', muneca: 'munequeras',
    /* Erratas reales de marca y producto, sacadas del catálogo. */
    bulpadel: 'bullpadel', bullpadell: 'bullpadel', bulpadell: 'bullpadel', bulpadel2: 'bullpadel',
    nocs: 'nox', noxx: 'nox', nos: 'nox',
    adiddas: 'adidas', adidass: 'adidas', addidas: 'adidas', adias: 'adidas',
    babolath: 'babolat', babolatt: 'babolat', babolar: 'babolat',
    sioux: 'siux', siuxx: 'siux',
    wilso: 'wilson', wilsson: 'wilson', willson: 'wilson',
    blackcrown: 'black crown', royalpadel: 'royal padel', kswiss: 'k-swiss',
    overgrpi: 'overgrip', ovegrip: 'overgrip', overgripp: 'overgrip', obergrip: 'overgrip',
    paletro: 'paletero', paletera: 'paletero',
    munyequera: 'munequera', muniequera: 'munequera',
    metalbon: 'metalbone', metabone: 'metalbone',
    vertx: 'vertex', pyton: 'piton',
    pelota: 'pelotas', bola: 'bolas'
  };

  /* Dos listas, no una. Antes iban juntas y por eso buscar "pala" a secas daba
     CERO resultados: 'pala' se descartaba como relleno, la consulta se quedaba
     sin palabras y el motor devolvía lista vacía.

     VACIAS son gramaticales y sobran siempre. GENERICAS nombran la categoría:
     sobran cuando hay algo más concreto ("pala Nox" -> "nox"), pero si son lo
     único que hay son la consulta ("pala" -> todas las palas). */
  const VACIAS = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'para',
    'con', 'un', 'una', 'unos', 'unas', 'mi', 'y', 'que', 'busco', 'quiero',
    'yo', 'se', 'va', 'tu', 'su', 'me', 'te', 'lo', 'al', 'en', 'es', 'ha', 'muy', 'mas',
    'algo', 'cosa', 'alguna', 'alguno', 'sobre', 'como', 'por']);

  const GENERICAS = new Set(['pala', 'palas', 'padel', 'producto', 'productos']);

  /* ---- Vocabulario del catálogo ----
     Todas las palabras que existen de verdad en títulos, marcas, tipos y tags,
     con su frecuencia. Lo construye `indexa()` una sola vez. Sirve para dos
     cosas: corregir erratas que no están en la tabla ERRATAS, y alimentar el
     autocompletado. */
  let VOCAB = null;

  /* Damerau-Levenshtein con corte: en cuanto la fila entera supera `max` se
     abandona, que es lo que hace viable comparar contra cientos de palabras en
     cada tecla. Cuenta transposiciones ("vetrex" -> "vertex") como un solo
     error, que es la errata de teclado más común. */
  const distancia = (a, b, max) => {
    const la = a.length;
    const lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    let ant2 = [];
    let ant = [];
    let fila = [];
    for (let j = 0; j <= lb; j++) ant[j] = j;
    for (let i = 1; i <= la; i++) {
      fila = [i];
      let mejor = i;
      for (let j = 1; j <= lb; j++) {
        const coste = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        let v = Math.min(ant[j] + 1, fila[j - 1] + 1, ant[j - 1] + coste);
        if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, ant2[j - 2] + 1);
        }
        fila[j] = v;
        if (v < mejor) mejor = v;
      }
      if (mejor > max) return max + 1;
      ant2 = ant;
      ant = fila;
    }
    return ant[lb];
  };

  /* Cuántos errores se le perdonan a una palabra según lo larga que sea. En 1-3
     letras NO se corrige nada: a esa longitud casi todo está a distancia 1 de
     todo y la "corrección" destroza la búsqueda en vez de arreglarla. */
  const margen = (w) => (w.length >= 7 ? 2 : w.length >= 4 ? 1 : 0);

  const CACHE_CORR = new Map();

  const corrigePalabra = (w) => {
    if (ERRATAS[w]) return ERRATAS[w];
    if (!VOCAB || VOCAB.has(w)) return w;
    if (CACHE_CORR.has(w)) return CACHE_CORR.get(w);
    const max = margen(w);
    if (!max) return w;
    let mejor = null;
    let mejorD = max + 1;
    let mejorF = 0;
    VOCAB.forEach((frec, v) => {
      if (Math.abs(v.length - w.length) > max) return;
      /* Con un solo error permitido exigimos misma inicial: casi ninguna errata
         real cambia la primera letra, y el filtro evita correcciones absurdas
         del tipo "nox" -> "box". */
      if (max < 2 && v.charAt(0) !== w.charAt(0)) return;
      const d = distancia(w, v, max);
      if (d > max) return;
      if (d < mejorD || (d === mejorD && frec > mejorF)) {
        mejor = v;
        mejorD = d;
        mejorF = frec;
      }
    });
    const res = mejor || w;
    CACHE_CORR.set(w, res);
    return res;
  };

  /* Trocea, limpia signos, quita vacías y corrige. Ya NO se descartan las
     palabras de 1-2 letras: son justo lo que se teclea al empezar a escribir, y
     tirarlas era lo que hacía salir "sin resultados" con dos letras para luego
     encontrar de sobra con la tercera. Lo que cambia con las cortas es cómo
     puntúan (ver `puntuaPalabra`), no si se usan. */
  const troceaBruto = (q) => sinTildes(q)
    .split(/\s+/)
    .map((w) => w.replace(/[^0-9a-z+.\-]/g, ''))
    .filter(Boolean);

  /* Separa lo que hay que BUSCAR de lo que solo REFUERZA.

     La genérica no se tira del todo: en "pala nox" se busca por "nox", pero
     "pala" sigue diciendo qué categoría quiere el cliente. Sin ese refuerzo,
     "pala nox" devolvía primero los botes de pelotas Nox (el título empieza por
     "Nox", 14 puntos) y las palas caían al fondo porque Nox solo les aparece
     como marca (5 puntos). Con el refuerzo, la categoría pedida sube. */
  const analiza = (q) => {
    const brutas = troceaBruto(q);
    if (!brutas.length) return { palabras: [], refuerzos: [] };
    /* Si la consulta ENTERA es una palabra vacía no se tira. Hay vacías de dos
       letras ("ha", "es", "va") que son justo el principio de un modelo:
       teclear "ha" buscando HACK devolvía cero mientras que "h" y "hac" iban
       bien, que es de las cosas más desconcertantes que puede hacer un
       buscador. */
    const sinVacias = brutas.filter((w) => !VACIAS.has(w));
    const base = (sinVacias.length ? sinVacias : brutas).map(corrigePalabra);
    const concretas = base.filter((w) => !GENERICAS.has(w));
    if (!concretas.length) return { palabras: base, refuerzos: [] };
    return { palabras: concretas, refuerzos: base.filter((w) => GENERICAS.has(w)) };
  };

  const utiles = (q) => analiza(q).palabras;

  /* Consulta corregida en texto, para poder avisar de "buscando X en vez de Y". */
  const corrige = (q) => utiles(q).join(' ');

  /* Intención -> tags reales del catálogo. Los conceptos (potencia, nivel, mujer)
     no aparecen en los títulos; cada entrada filtra por tag, donde sí viven. */
  const INTENCION = [
    { claves: ['potencia', 'pegada', 'ataque', 'atacar'], tags: ['Juego: Potencia'], etiqueta: 'palas de potencia' },
    { claves: ['control', 'defensa', 'defensiva', 'defender'], tags: ['Juego: Control'], etiqueta: 'palas de control' },
    { claves: ['polivalente', 'polivalentes', 'equilibrada'], tags: ['Juego: Polivalente'], etiqueta: 'palas polivalentes' },
    { claves: ['iniciacion', 'principiante', 'principiantes', 'empezar', 'novato'], tags: ['Nivel: Iniciación', 'Nivel: Iniciación-Intermedio'], etiqueta: 'palas de iniciación' },
    { claves: ['intermedio'], tags: ['Nivel: Intermedio', 'Nivel: Iniciación-Intermedio', 'Nivel: Intermedio-Avanzado'], etiqueta: 'palas de nivel intermedio' },
    { claves: ['avanzado', 'competicion', 'profesional'], tags: ['Nivel: Avanzado', 'Nivel: Intermedio-Avanzado', 'Nivel: Profesional'], etiqueta: 'palas avanzadas' },
    { claves: ['mujer', 'mujeres', 'femenina', 'chica'], tags: ['Mujer'], etiqueta: 'palas de mujer' },
    { claves: ['redonda', 'redondas'], tags: ['Forma: Redonda'], etiqueta: 'palas redondas' },
    { claves: ['lagrima'], tags: ['Forma: Lágrima'], etiqueta: 'palas de lágrima' },
    { claves: ['diamante'], tags: ['Forma: Diamante'], etiqueta: 'palas de diamante' },
    { claves: ['hibrida', 'hibridas'], tags: ['Forma: Híbrida'], etiqueta: 'palas híbridas' },
    { claves: ['codo', 'hombro', 'lesion', 'lesiones'], tags: ['Prevención de lesiones'], etiqueta: 'prevención de lesiones' },
    { claves: ['paletero', 'paleteros', 'mochila', 'mochilas', 'bolsa', 'bolsas'], tags: ['Paleteros'], tipos: ['Paleteros', 'Paletero', 'Mochilas'], etiqueta: 'paleteros y mochilas' },
    { claves: ['neceser', 'neceseres'], tipos: ['Neceseres'], etiqueta: 'neceseres' },
    { claves: ['munequera', 'munequeras'], tags: ['Muñequeras'], tipos: ['Muñequeras'], etiqueta: 'muñequeras' },
    { claves: ['grip', 'grips', 'overgrip', 'overgrips', 'agarre'], tags: ['Grips'], tipos: ['Grips'], etiqueta: 'grips y overgrips' },
    { claves: ['pelota', 'pelotas', 'bola', 'bolas', 'bote'], tags: ['Bolas'], tipos: ['Bolas'], etiqueta: 'bolas de pádel' },
    { claves: ['voley', 'voleibol', 'balon', 'balones'], tags: ['Voley'], tipos: ['Balones', 'Rodilleras Voley'], etiqueta: 'voleibol' },
    { claves: ['rodillera', 'rodilleras'], tipos: ['Rodilleras', 'Rodilleras Voley'], etiqueta: 'rodilleras' },
    { claves: ['pack', 'packs'], tags: ['Packs'], tipos: ['Packs'], etiqueta: 'packs' },
    { claves: ['accesorio', 'accesorios'], tags: ['Accesorios'], tipos: ['Accesorios', 'Presurizadores'], etiqueta: 'accesorios' }
  ];

  /* La intención solo se aplica si TODA la consulta es genérica. Si aparece algo
     específico (un modelo, una marca), buscan un producto concreto y manda el texto. */
  const intencionDe = (q) => {
    const palabras = utiles(q);
    if (!palabras.length) return null;
    return INTENCION.find((i) => {
      const claves = i.claves.map(sinTildes);
      return palabras.some((w) => claves.includes(w)) && palabras.every((w) => claves.includes(w));
    });
  };

  /* ---- Catálogo ---- */
  let CATALOGO = null;
  let RECO = null;

  const reduce = (p) => {
    const v = (p.variants || [])[0] || {};
    return {
      t: p.title,
      h: p.handle,
      v: p.vendor || '',
      y: p.product_type || '',
      g: p.tags || [],
      p: v.price,
      c: v.compare_at_price,
      a: (p.variants || []).some((x) => x.available),
      i: ((p.images || [])[0] || {}).src || null
    };
  };

  /* Texto normalizado por producto, para no recalcularlo en cada tecla.
     De paso se levanta el vocabulario: cada palabra que aparece de verdad en el
     catálogo, pesada por dónde sale. El título pesa más que un tag porque a la
     hora de corregir una errata interesa caer en un nombre de producto real
     ("metabone" -> "metalbone") antes que en una etiqueta interna. */
  const indexa = (items) => {
    const vocab = new Map();
    const suma = (texto, peso) => {
      sinTildes(texto).split(/[^0-9a-z+.\-]+/).forEach((w) => {
        if (w.length < 3) return;
        vocab.set(w, (vocab.get(w) || 0) + peso);
      });
    };
    const res = items.map((p) => {
      suma(p.t, 4);
      suma(p.v, 3);
      suma(p.y, 2);
      p.g.forEach((t) => suma(t, 1));
      return Object.assign({}, p, {
        _t: sinTildes(p.t),
        _v: sinTildes(p.v),
        _y: sinTildes(p.y),
        _g: p.g.map(sinTildes).join(' ')
      });
    });
    VOCAB = vocab;
    CACHE_CORR.clear();
    return res;
  };

  const carga = async () => {
    if (CATALOGO) return CATALOGO;
    try {
      const guardado = sessionStorage.getItem('ph_catalogo');
      if (guardado) {
        const d = JSON.parse(guardado);
        if (Date.now() - d.ts < CACHE_MS && Array.isArray(d.items)) {
          CATALOGO = indexa(d.items);
          return CATALOGO;
        }
      }
    } catch (_) { /* sessionStorage no disponible */ }

    const items = [];
    for (let pag = 1; pag <= 6; pag++) {
      const r = await fetch(`${ROOT}/products.json?limit=250&page=${pag}`, { headers: { Accept: 'application/json' } });
      if (!r.ok) break;
      const d = await r.json();
      if (!d.products || !d.products.length) break;
      items.push(...d.products.map(reduce));
      if (d.products.length < 250) break;
    }
    try { sessionStorage.setItem('ph_catalogo', JSON.stringify({ ts: Date.now(), items })); } catch (_) {}
    CATALOGO = indexa(items);
    return CATALOGO;
  };

  /* Vendible de verdad: hay stock Y tiene precio. El catálogo arrastra varios
     productos de relleno a 0,00 € (la Valkiria sin tipo, el Racketbag Control,
     el Shoe Bag) que están "disponibles" pero no se pueden recomendar. */
  const vendible = (p) => p.a && parseFloat(p.p) > 0;

  /* Round-robin por marca. Sin esto, el grupo de accesorios se llena con cuatro
     overgrips Bullpadel casi idénticos, porque el catálogo trae veinte seguidos
     de la misma marca. Así sale uno de cada marca antes de repetir. */
  const porMarca = (lista) => {
    const cubos = new Map();
    lista.forEach((p) => {
      const k = p.v || '-';
      if (!cubos.has(k)) cubos.set(k, []);
      cubos.get(k).push(p);
    });
    const out = [];
    let quedan = true;
    while (quedan) {
      quedan = false;
      cubos.forEach((arr) => {
        if (arr.length) { out.push(arr.shift()); quedan = true; }
      });
    }
    return out;
  };

  /* Reparte dentro de un grupo: primero alterna TIPOS, y dentro de cada tipo
     alterna MARCAS. Sin el reparto por tipo, el grupo de complementos se llenaba
     con tres overgrips (de marcas distintas, pero tres overgrips) y no salía
     ninguna muñequera ni ningún accesorio. */
  const reparte = (lista, tipos) => {
    const cubos = tipos.map((t) => porMarca(lista.filter((p) => p.y === t)));
    const out = [];
    let quedan = true;
    while (quedan) {
      quedan = false;
      cubos.forEach((arr) => {
        if (arr.length) { out.push(arr.shift()); quedan = true; }
      });
    }
    return out;
  };

  /* Recomendados del estado vacío: mezcla por tipo según RECO_GRUPOS.
     Las palas siguen saliendo de la colección destacada, para que se elijan
     desde Shopify sin tocar código; el resto sale del catálogo ya cargado, así
     que se ajusta solo cuando cambia el stock. */
  const recomendados = async () => {
    if (RECO) return RECO;
    const cat = await carga();

    let palas = [];
    try {
      const r = await fetch(`${ROOT}/collections/${COLECCION_RECO}/products.json?limit=20`, { headers: { Accept: 'application/json' } });
      if (r.ok) {
        const d = await r.json();
        if (d.products && d.products.length) palas = d.products.map(reduce).filter(vendible);
      }
    } catch (_) { /* cae al catálogo */ }
    if (!palas.length) palas = cat.filter((p) => vendible(p) && /pala/i.test(p.y));

    const vistos = new Set();
    const out = [];
    const mete = (lista, cupo) => {
      for (const p of lista) {
        if (out.length >= RECO_TOTAL || cupo <= 0) break;
        if (vistos.has(p.h)) continue;
        vistos.add(p.h);
        out.push(p);
        cupo--;
      }
    };

    RECO_GRUPOS.forEach((g) => {
      /* Las palas van por marca a secas: vienen de la colección y no conviene
         filtrarlas por product_type, que ahí puede venir vacío. */
      const fuente = g.tipos.indexOf('Palas de pádel') !== -1
        ? porMarca(palas)
        : reparte(cat.filter(vendible), g.tipos);
      mete(fuente, g.n);
    });

    /* Grupos sin stock (hoy casi todas las mochilas y paleteros están a cero):
       se rellena con palas para no dejar huecos en la rejilla. */
    mete(palas, RECO_TOTAL - out.length);

    RECO = out.slice(0, RECO_TOTAL);
    return RECO;
  };

  /* ¿`texto` tiene alguna PALABRA que empiece por `w`? No vale a media palabra. */
  const empiezaPor = (texto, w) => texto.startsWith(w) || texto.indexOf(' ' + w) !== -1;

  /* Puntúa por dónde aparece la palabra: el título manda sobre la marca,
     y la marca sobre el tipo y los tags.

     Las palabras de 1-2 letras solo cuentan como PRINCIPIO de palabra. Si
     valieran como subcadena, teclear "o" devolvería medio catálogo y el
     buscador sería inútil justo en la primera letra. Con principio de palabra,
     "no" encuentra Nox, "at" encuentra AT10 y "ve" encuentra Vertex y Veron,
     que es exactamente lo que se espera al empezar a escribir. */
  const puntuaPalabra = (p, w) => {
    if (w.length <= 2) {
      if (p._t.startsWith(w)) return 14;
      if (empiezaPor(p._t, w)) return 11;
      if (empiezaPor(p._v, w)) return 6;
      if (empiezaPor(p._y, w)) return 3;
      return 0;
    }
    if (p._t.startsWith(w)) return 14;
    if (empiezaPor(p._t, w)) return 11;
    if (p._t.includes(w)) return 8;
    if (p._v.includes(w)) return 5;
    if (p._y.includes(w)) return 3;
    if (p._g.includes(w)) return 2;
    return 0;
  };

  /* estricto = deben aparecer todas las palabras. Da los mejores resultados,
     pero se queda en cero con consultas mixtas tipo "vertex control"; por eso
     hay un segundo intento parcial. */
  const puntua = (p, palabras, estricto) => {
    let total = 0;
    let aciertos = 0;
    for (const w of palabras) {
      const s = puntuaPalabra(p, w);
      if (!s && estricto) return 0;
      if (s) aciertos++;
      total += s;
    }
    if (!aciertos) return 0;
    return total + aciertos * 2 + (p.a ? 1 : 0); // a igualdad, primero lo disponible
  };

  const busca = (bruto) => {
    if (!CATALOGO) return { items: [], etiqueta: null };
    const intencion = intencionDe(bruto);
    if (intencion) {
      /* Tag O product_type. Los conceptos de pala (juego, nivel, forma, mujer)
         solo viven en tags, pero las CATEGORÍAS viven en el product_type y el
         tag correspondiente puede faltar — de hecho las mochilas no llevan tag
         'Paleteros', así que buscando "mochila" no salía ninguna. Mirando los
         dos campos, la categoría se encuentra aunque el etiquetado falle. */
      const tags = (intencion.tags || []).map(sinTildes);
      const tipos = (intencion.tipos || []).map(sinTildes);
      const res = CATALOGO.filter((p) =>
        (tags.length && p.g.some((t) => tags.includes(sinTildes(t)))) ||
        (tipos.length && tipos.includes(p._y)));
      return { items: res, etiqueta: intencion.etiqueta };
    }
    const analisis = analiza(bruto);
    const palabras = analisis.palabras;
    const refuerzos = analisis.refuerzos;
    if (!palabras.length) return { items: [], etiqueta: null };

    /* Si se ha corregido algo, se devuelve para poder decirlo en pantalla: el
       cliente tiene que enterarse de que está viendo otra cosa de lo que tecleó. */
    const tecleadas = troceaBruto(bruto).filter((w) => !VACIAS.has(w));
    const correccion = palabras.join(' ') !== tecleadas.join(' ') ? palabras.join(' ') : null;

    /* La categoría pedida ("pala", "padel") NO filtra, PARTE: primero todo lo
       que es de ese tipo, ordenado por puntuación, y detrás el resto. Se hace
       partiendo y no sumando puntos porque los puntos no daban: un bote de
       pelotas cuyo título empieza por "Nox" saca 14 y una pala cuya marca es
       Nox saca 5, así que cualquier bonus razonable se quedaba corto. Y
       filtrar de verdad escondería resultados buenos cuando el product_type
       esté mal puesto, que en este catálogo pasa. */
    const refuerza = (p) => (refuerzos.some((g) => p._y.indexOf(g) !== -1) ? 1 : 0);

    const ordena = (estricto) => CATALOGO
      .map((p) => ({ p: p, s: puntua(p, palabras, estricto), r: refuerza(p) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => (b.r - a.r) || (b.s - a.s))
      .map((x) => x.p);

    const exactos = ordena(true);
    if (exactos.length || palabras.length < 2) return { items: exactos, etiqueta: null, correccion: correccion };
    /* Nada con todas las palabras: lo más parecido antes que una pantalla vacía. */
    return { items: ordena(false), etiqueta: null, parcial: true, correccion: correccion };
  };

  /* ---- Autocompletado ----
     Devuelve hasta `n` sugerencias para lo que se lleva tecleado, en orden de
     utilidad: primero marcas (una palabra, llevan a mucho catálogo), luego
     productos cuyo título ARRANCA por lo tecleado, luego productos donde alguna
     palabra del título arranca por ahí. Solo si no hay nada por prefijo se
     recurre a la corrección de erratas, para que escribir mal no deje el
     desplegable en blanco. */
  const sugiere = (bruto, n) => {
    const tope = n || 6;
    if (!CATALOGO) return [];
    const q = sinTildes(bruto);
    if (!q) return [];

    const out = [];
    const vistos = new Set();
    const mete = (texto, tipo) => {
      const k = sinTildes(texto);
      if (!k || vistos.has(k) || out.length >= tope) return;
      vistos.add(k);
      out.push({ texto: texto, tipo: tipo });
    };

    vendors().forEach((v) => { if (sinTildes(v).startsWith(q)) mete(v, 'Marca'); });
    CATALOGO.forEach((p) => { if (vendible(p) && p._t.startsWith(q)) mete(p.t, p.v); });
    CATALOGO.forEach((p) => { if (vendible(p) && empiezaPor(p._t, q)) mete(p.t, p.v); });

    if (!out.length) {
      const c = corrigePalabra(q);
      if (c !== q) {
        CATALOGO.forEach((p) => {
          if (vendible(p) && (empiezaPor(p._t, c) || p._t.indexOf(c) !== -1)) mete(p.t, p.v);
        });
      }
    }
    return out.slice(0, tope);
  };

  /* ---- Facetas (chips de filtro) ---- */
  const FACETAS = [
    { key: 'marca', label: 'Marca', tipo: 'vendor', opciones: null }, // opciones dinámicas: vendors()
    { key: 'nivel', label: 'Nivel', opciones: [
      { v: 'Iniciación', tags: ['Nivel: Iniciación', 'Nivel: Iniciación-Intermedio'] },
      { v: 'Intermedio', tags: ['Nivel: Intermedio', 'Nivel: Iniciación-Intermedio', 'Nivel: Intermedio-Avanzado'] },
      { v: 'Avanzado', tags: ['Nivel: Avanzado', 'Nivel: Intermedio-Avanzado', 'Nivel: Profesional'] }
    ] },
    { key: 'juego', label: 'Juego', opciones: [
      { v: 'Control', tags: ['Juego: Control'] },
      { v: 'Potencia', tags: ['Juego: Potencia'] },
      { v: 'Polivalente', tags: ['Juego: Polivalente'] }
    ] },
    { key: 'forma', label: 'Forma', opciones: [
      { v: 'Redonda', tags: ['Forma: Redonda'] },
      { v: 'Lágrima', tags: ['Forma: Lágrima'] },
      { v: 'Diamante', tags: ['Forma: Diamante'] },
      { v: 'Híbrida', tags: ['Forma: Híbrida'] }
    ] }
  ];

  const vendors = () => {
    if (!CATALOGO) return [];
    const vistos = new Map();
    for (const p of CATALOGO) {
      if (p.v) vistos.set(p.v, (vistos.get(p.v) || 0) + 1);
    }
    return [...vistos.keys()].sort((a, b) => a.localeCompare(b, 'es'));
  };

  /* sel = {marca: 'Nox'|null, nivel: 'Avanzado'|null, juego: ..., forma: ...} */
  const filtra = (items, sel) => {
    if (!sel) return items;
    let res = items;
    if (sel.marca) res = res.filter((p) => p.v === sel.marca);
    for (const f of FACETAS) {
      if (f.tipo === 'vendor' || !sel[f.key]) continue;
      const op = f.opciones.find((o) => o.v === sel[f.key]);
      if (!op) continue;
      const tags = op.tags.map(sinTildes);
      res = res.filter((p) => p.g.some((t) => tags.includes(sinTildes(t))));
    }
    return res;
  };

  /* ---- Presentación ---- */
  /* Formato de la home ("€339,99"): símbolo delante, decimales con coma. */
  const formatEuro = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return '';
    try { return '€' + new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n); }
    catch (_) { return '€' + n.toFixed(2).replace('.', ','); }
  };

  const imgUrl = (src, w) => {
    if (!src) return null;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + (w || 360);
  };

  window.PHSearch = {
    setRoot: (r) => { ROOT = String(r || '').replace(/\/$/, ''); },
    carga, busca, filtra, FACETAS, vendors, recomendados, formatEuro, imgUrl, corrige, sinTildes, sugiere
  };
})();
